
import Vue from 'vue';
import {
	TeamMember,
	CompanyCustomFieldDef,
} from '@run-diffusion/shared';
import TeamMemberTeamRoleTableCell from '@/views/Team/TeamDashboard/TeamMemberTeamRoleTableCell.vue';
import { get$bindFirestoreOptions } from '@/mixins';
import { db } from '@/firebase';
import { TEAM_ROLE_DETAILS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import { mapGetters, mapState } from 'vuex';
import TeamMemberJoinedStatus from '@/views/Team/TeamMemberJoinedStatus.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import { TeamUserMixin } from '@/mixins';
import ClipboardCopy from '@/components/ClipboardCopy.vue';

export default Vue.extend({
	name: 'TeamMemberTeamRow',
	props: {
		dashboardTeamMember: { type: Object, default: null },
		currTeam: { type: Object, required: true },
		teamUserRoleDefs: { type: Array, default: () => [] },
		// The list's call, so the cell and its header always agree (none on deactivated Teams)
		showTeamRoleColumn: { type: Boolean, default: false },
	},
	data () {
		return {
			TEAM_ROLE_DETAILS,
			retryLoadDataCount: 0,
			MAX_RETRY_LOAD_DATA_COUNT: 2,

			loadingLocalTeamMember: false,
			localTeamMember: null,
			// The membership loaded and then went away: the row hides, and comes back if it returns
			membershipGone: false,

			MORE_ACTIONS_ITEM_VALUE: {
				EDIT: 'edit',
				DELETE: 'delete',
			},
		};
	},
	mixins: [
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
		]),
		teamCustomFieldDefs(): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		isSelfMember () {
			return !!(this.user && this.user.id === this.dashboardTeamMember.userId);
		},
		moreActionsMenuItems () {
			if (this.scimProvisioningLocksMembership) return [];
			let idIncrementer: number = 0;
			return [
				...(this.hasManageTeamsPermission && this.currTeam?.isActive ? [
					{
						id: idIncrementer++,
						icon: 'mdi-shield-account-outline',
						label: `Update Role`,
						description: `Change ${this.displayLabels.team} role`,
						value: this.MORE_ACTIONS_ITEM_VALUE.EDIT,
						isUpsell: !this.companyClubOfferBenefits.isEnterprise,
					},
				] : []),
				...(this.hasManageTeamsPermission && !this.isSelfMember ? [
					{
						id: idIncrementer++,
						icon: 'mdi-account-minus-outline',
						iconStyle: { color: 'var(--red-500)' },
						descriptionStyle: { color: 'var(--grey-400)' },
						label: `Remove From ${this.displayLabels.team}`,
						description: `They will lose access to this ${this.displayLabels.team}`,
						value: this.MORE_ACTIONS_ITEM_VALUE.DELETE,
					},
				] : []),
			];
		},
	},
	async created () {
		await this.bindLocalTeamMember(this.dashboardTeamMember);
	},
	watch: {
		localTeamMember (newVal: TeamMember, oldVal: TeamMember) {
			// A rebind clears it while loading, which is not a removal
			if (this.loadingLocalTeamMember) return;
			if (oldVal && !newVal) {
				this.membershipGone = true;
				this.$emit('membership-removed', this.currTeam?.id || null);
			} else if (newVal) {
				this.membershipGone = false;
			}
		},
		dashboardTeamMember: {
			immediate: false,
			async handler (newVal: TeamMember, oldVal: TeamMember) {
				const newMemberId: string = newVal?.id;
				const oldMemberId: string = oldVal?.id;
				if (newMemberId !== oldMemberId) {
					await this.bindLocalTeamMember(newVal);
				}
			},
		},
	},
	methods: {
		async onRetryLoadData () {
			if (this.retryLoadDataCount >= this.MAX_RETRY_LOAD_DATA_COUNT) {
				// Navigate back to company dashboard after failed attempts
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.COMPANY_DASHBOARD,
					query: this.$route.query,
				});
				return;
			}

			this.retryLoadDataCount++;
			await this.bindLocalTeamMember(this.dashboardTeamMember);
		},
		async bindLocalTeamMember (dashboardTeamMember: TeamMember) {
			if (
				!dashboardTeamMember ||
				this.loadingLocalTeamMember
			) {
				return;
			}

			try {
				// Bind team member data for this specific team
				this.localTeamMember = null;
				this.loadingLocalTeamMember = true;
				await this.$bind(
					'localTeamMember',
					db
						.collection('teams')
						.doc(this.currTeam.id)
						.collection('teamMembers')
						.doc(dashboardTeamMember.id),
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding team member data:', error);
			} finally {
				this.loadingLocalTeamMember = false;
			}
		},
		onViewTeam () {
			if (!this.currTeam?.id) return;

			this.routerPush(this.$route, this.$router, {
				name: ROUTER.TEAM_DASHBOARD,
				params: {
					teamId: this.currTeam.id,
				},
				query: this.$route.query,
			});
		},
		onSelectedMoreActions (moreActionsItemValue: string) {
			switch (moreActionsItemValue) {
				case this.MORE_ACTIONS_ITEM_VALUE.EDIT:
					this.$emit('edit-team-member-teams', { teams: [this.currTeam], teamMember: this.localTeamMember });
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.DELETE:
					this.$emit('remove-team-members', { teamMembers: [this.localTeamMember], team: this.currTeam });
					break;
				default:
					break;
			}
		},
	},
	components: {
		ClipboardCopy,
		TeamMemberJoinedStatus,
		BaseStyledMenu,
		TeamMemberTeamRoleTableCell,
	},
});
